import { type ValueOf } from '@/types';
import { type GetStartedCardsExtraProps } from '@/features/HelpStation/types';

export const ChecklistType = {
	PROJECT_CHECKLIST: 'project_checklist',
	GET_STARTED_CHECKLIST: 'get_started_checklist',
	SET_UP_SITE_CHECKLIST: 'set_up_site_checklist',
	LEARN_MORE_CHECKLIST: 'learn_more_checklist',
} as const;

export const GetStartedActionCards = {
	site_created: 'site_created',
	discover_card: 'discover_card',
	add_user: 'add_user',
	project_card: 'project_card',
	delete_demo_data: 'delete_demo_data',
	set_up_site_card: 'set_up_site_card',
	learn_more_card: 'learn_more_card',
};

export const ProjectChecklistCards = {
	create_estimate: 'create_estimate',
	create_project: 'create_project',
	review_bookings: 'review_bookings',
	create_task: 'create_task',
	create_invoice: 'create_invoice',
};

export const SetUpSiteChecklistCards = {
	user_permissions: 'user_permissions',
	set_pricing: 'set_pricing',
	set_laborcost: 'set_laborcost',
	add_products: 'add_products',
	add_activity_types: 'add_activity_types',
	set_pdf: 'set_pdf',
	set_integrations: 'set_integrations',
};

export const LearnMoreChecklistCards = {
	benefit_from_workflow_alerts: 'benefit_from_workflow_alerts',
	discover_business_insights: 'discover_business_insights',
	explore_integrations: 'explore_integrations',
	learn_from_success_stories: 'learn_from_success_stories',
};

export const Views = {
	MAIN_VIEW: 'HelpStationMainView',
	GET_STARTED_VIEW: 'TrialGuideGetStartedView',
	PROJECT_CHECKLIST_VIEW: 'TrialGuideProjectChecklistView',
	SET_UP_SITE_CHECKLIST_VIEW: 'TrialGuideSetUpSiteChecklistView',
	CHAT_VIEW: 'HelpStationChatView',
	LEARN_MORE_CHECKLIST_VIEW: 'TrialGuideLearnMoreChecklistView',
};

export const GetStartedViews = [
	Views.GET_STARTED_VIEW,
	Views.PROJECT_CHECKLIST_VIEW,
	Views.SET_UP_SITE_CHECKLIST_VIEW,
	Views.LEARN_MORE_CHECKLIST_VIEW,
];

export const TRIAL_CREATOR_USER_ID = 1;

export const SUCCESS_MESSAGE_LANG_VARS = [
	'get_started_success_message_1',
	'get_started_success_message_2',
	'get_started_success_message_3',
	'get_started_success_message_4',
	'get_started_success_message_5',
	'get_started_success_message_6',
	'get_started_success_message_7',
];

export const PERCENTAGE_VALUES_FOR_SITE_CREATOR = new Map<
	ValueOf<typeof GetStartedActionCards>,
	number
>([
	[GetStartedActionCards.site_created, 10],
	[GetStartedActionCards.discover_card, 20],
	[GetStartedActionCards.add_user, 10],
	[GetStartedActionCards.delete_demo_data, 20],
	[GetStartedActionCards.project_card, 20],
	[GetStartedActionCards.set_up_site_card, 20],
]);

export const PERCENTAGE_VALUES_FOR_SECONDARY_USER_ADMIN = new Map<
	ValueOf<typeof GetStartedActionCards>,
	number
>([
	[GetStartedActionCards.site_created, 10],
	[GetStartedActionCards.add_user, 20],
	[GetStartedActionCards.project_card, 50],
	[GetStartedActionCards.learn_more_card, 20],
]);

export const PERCENTAGE_VALUES_FOR_SECONDARY_USER_NOT_ADMIN = new Map<
	ValueOf<typeof GetStartedActionCards>,
	number
>([
	[GetStartedActionCards.site_created, 10],
	[GetStartedActionCards.project_card, 60],
	[GetStartedActionCards.learn_more_card, 30],
]);

export const COMPANY_SIZE_1_TO_4 = '1-4 people';
export const COMPANY_SIZE_5_TO_9 = '5-9 people';

export const MILESTONE_1 = 'milestone_1';
export const MILESTONE_2 = 'milestone_2';

export const PROGRESS_BAR_PERCENTAGE_PLACEMENT_TOP = 'top';

export const PERMISSION_SET_SPECIALIST = 'Specialist';

export const ACTION_CARDS_BUTTON_TYPE_REGULAR = 'regular';
export const ACTION_CARDS_BUTTON_TYPE_INLINE = 'inline';

export const GET_STARTED_ACTION_CARD_EXTRA_PROPERTIES: {
	[key: ValueOf<typeof GetStartedActionCards>]: GetStartedCardsExtraProps;
} = {
	[GetStartedActionCards.discover_card]: {
		buttonType: ACTION_CARDS_BUTTON_TYPE_REGULAR,
		headerText: 'get_started_discover_card_title_virtual_demo',
		buttonText: 'get_started_discover_card_title_cta_start_tour',
		buttonIcon: 'icon-play-16',
	},
	[GetStartedActionCards.project_card]: {
		buttonType: ACTION_CARDS_BUTTON_TYPE_INLINE,
		headerText: undefined,
		buttonText: 'get_started_project_card_cta',
		buttonIcon: undefined,
	},
	[GetStartedActionCards.add_user]: {
		buttonType: ACTION_CARDS_BUTTON_TYPE_REGULAR,
		headerText: 'get_started_add_user_title',
		buttonText: 'get_started_add_user_cta',
		buttonIcon: 'icon-addperson-16',
	},
	[GetStartedActionCards.set_up_site_card]: {
		buttonType: ACTION_CARDS_BUTTON_TYPE_INLINE,
		headerText: undefined,
		buttonText: 'get_started_set_up_site_card_cta_customize_site',
		buttonIcon: undefined,
	},
	[GetStartedActionCards.delete_demo_data]: {
		buttonType: ACTION_CARDS_BUTTON_TYPE_REGULAR,
		headerText: 'get_started_delete_demo_data_title',
		buttonText: 'get_started_delete_demo_data_cta',
		buttonIcon: 'icon-bin-16',
	},
	[GetStartedActionCards.learn_more_card]: {
		buttonType: ACTION_CARDS_BUTTON_TYPE_INLINE,
		headerText: undefined,
		buttonText: 'get_started_learn_more_about_scoro_card_cta',
		buttonIcon: undefined,
	},
};

export const SUPPORT_REQUEST_URL =
	'https://support.scoro.com/hc/en-us/requests/new?ticket_form_id=8090031183245';
